import { AppService } from "./appService";
import { BootService } from "./bootService";
import { ConfigService } from "./configService";
import { WorkspaceService } from "./workspaceService";
import { WindowService } from "./windowService";
import { StorageService } from "./storageService";
import { AuthenticationService } from "./authenticationService";
import { DistributedStoreService } from "./distributedStoreService";
import { NotificationService } from "./notificationService";
import { HotkeyService } from "./hotkeyService";
import { LoggerService } from "./loggerService";
import { ProtocolHandlerService } from "./protocolHandlerService";
import { DataMigration } from "./dataMigration";
import { Router } from "../../api-polyfill/src/router/router";
import { toGoLangInvoke, AsyncConstructors, FINSEMBLECONFIG, FINSEMBLESERVICECONTEXT, safeInteropRegister } from "../../api-polyfill/src/common/windowFunctions";
import { PromiseWrapper } from "../../api-polyfill/src/utils/pw";
import { createLogger } from "../../api-polyfill/src/common/Logger";
import { LoggerImpl } from "../../api-polyfill/src/logger";
export { ConfigService };
const waitForPostSSOStages = async ({ glue, logger, workspaceService, storageService, appService, configService, notificationService, manifest }) => {
    const pw1 = new PromiseWrapper();
    const pw2 = new PromiseWrapper();
    const runPostSSOStage = async () => {
        logger.debug("boot-sequence - runPostSSOStage: Received post-sso boot stage signal.");
        await glue.contexts.set("IO.Global.InUserStage", { inUserStage: true });
        const auth = await globalThis.glue42gd.getAuth();
        const { user } = auth ?? {};
        if (user) {
            await storageService.setUser({ user });
        }
        logger.debug("boot-sequence - runPostSSOStage: Calling appService.loadUserDefinedApps()");
        await appService.loadUserDefinedApps();
        const dataMigration = new DataMigration(glue, manifest, logger);
        await dataMigration.run();
        await configService.maybeSetScheduledShutdownAndRestart();
        await notificationService.initialize();
        pw1.resolve();
        logger.debug("boot-sequence - runPostSSOStage: Returning response to EnterBootStage app");
    };
    const runUserStage = async () => {
        logger.debug("boot-sequence - runUserStage: Received user boot stage signal.");
        logger.debug("boot-sequence - runUserStage: Calling workspaceService.loadWorkspacesPostSSO()");
        await workspaceService.loadWorkspacesPostSSO(manifest);
        logger.debug("boot-sequence - runUserStage: Setting context IO.Global.InUserStage to release FSBL preferences API");
        pw2.resolve();
        logger.debug("boot-sequence - runUserStage: Returning response to EnterBootStage app");
    };
    const runBootStage = async (stage) => {
        if (stage === "post-sso") {
            await runPostSSOStage();
        }
        else if (stage === "user") {
            await runUserStage();
        }
        else {
            logger.error(`boot-sequence - Received FSBL.enterBootStage for unknown stage "${stage}"`);
        }
    };
    await glue.interop.register("FSBL.enterBootStage", safeInteropRegister(async (data) => runBootStage(data?.stage), logger));
    await pw1.promise;
    await pw2.promise;
};
export const startServices = async (glue, serviceConfig, logger, asyncConstructors) => {
    let onErrorMakeSystemManagerVisible = true;
    const timeout = serviceConfig?.timeout ?? 40000;
    const timer = setInterval(() => {
        if (glue.windows.list().map((w) => w.name).includes("sso-application")) {
            logger.info(`boot-sequence - sso-application is running so timeout has been reset another ${timeout} milliseconds.`);
            return;
        }
        clearInterval(timer);
        if (onErrorMakeSystemManagerVisible) {
            glue.windows.my()?.show();
        }
        else {
            glue.notifications.raise({ title: "A system error has occurred during startup. Please check finsemble-service console for errors." });
        }
        const error = `boot-sequence - fsbl-service is taking over ${timeout} milliseconds to start. Check for dev console errors or increase timeout in manifest under "iocd.timeout".`;
        logger.error(error);
        console.error(error);
        document.getElementById("errorMessage").innerText = error;
    }, timeout);
    logger.debug("boot-sequence - Entering pre-sso initialization");
    const preloadURL = new URL("../../../api-polyfill/dist/fsbl.umd.js", document.location.href).href;
    const router = new Router(new Promise((resolve) => { resolve(glue); }), asyncConstructors, logger.loggerClient);
    const loggerService = new LoggerService(glue, logger, router);
    const bootService = new BootService(glue, logger, router);
    const configService = new ConfigService(glue, logger, serviceConfig);
    const storageService = new StorageService(glue, logger);
    const workspaceService = new WorkspaceService(glue, logger, router);
    const windowService = new WindowService(glue, logger, router);
    const authenticationService = new AuthenticationService(glue, logger, configService, configService.config?.finsemble?.moduleRoot);
    const distributedStoreService = new DistributedStoreService(glue, logger);
    const notificationService = new NotificationService(glue, logger, router);
    const hotkeyService = new HotkeyService(glue, logger);
    const protocolHandlerService = new ProtocolHandlerService(glue, logger);
    const appService = new AppService(glue, logger, router);
    await loggerService.start();
    await bootService.start();
    await protocolHandlerService.start();
    await configService.start();
    const manifest = await glue.contexts.get(FINSEMBLECONFIG);
    logger.debug("boot-sequence - MANIFEST", manifest);
    const uuid = manifest?.startup_app?.uuid ?? "Finsemble";
    onErrorMakeSystemManagerVisible = manifest.finsemble?.bootConfig?.onErrorMakeSystemManagerVisible ?? true;
    logger.debug("boot-sequence - distributedStoreService start");
    await distributedStoreService.start();
    logger.debug("boot-sequence - storageService start");
    await storageService.start({ uuid, manifest });
    logger.debug("boot-sequence - windowService start");
    await windowService.start({ manifest });
    logger.debug("boot-sequence - workspaceService start");
    await workspaceService.start();
    logger.debug("boot-sequence - authenticationService start");
    await authenticationService.start(preloadURL);
    logger.debug("boot-sequence - appService start");
    await appService.start();
    logger.debug("boot-sequence - notificationService start");
    await notificationService.start();
    logger.debug("boot-sequence - hotkeyService start");
    await hotkeyService.start(manifest);
    logger.debug("boot-sequence - maybeSetLogToDisk");
    await loggerService.maybeSetLogToDisk(manifest);
    const { err } = await toGoLangInvoke(() => glue.interop.invoke("fsbl-service-ready-for-user-stage", {}));
    if (err) {
        logger.error("boot-sequence - Error calling fsbl-service-ready-for-user-stage", err);
    }
    logger.debug("boot-sequence - pre-sso initialization complete. Now waiting to enter post-sso boot stage.");
    await waitForPostSSOStages({ glue, logger, configService, workspaceService, storageService, appService, notificationService, manifest });
    logger.debug("boot-sequence - post-sso initialization complete. finsemble-service is now fully operational.");
    clearInterval(timer);
};
export const main = async (glue) => {
    glue.interop.invoke("fsbl-service-started", {});
    const finsembleServiceContext = await glue.windows.my()?.getContext();
    const asyncConstructors = new AsyncConstructors();
    const loggerClient = new LoggerImpl(new Promise((resolve) => { resolve(glue); }), asyncConstructors);
    const logger = createLogger(glue, loggerClient);
    if (!finsembleServiceContext) {
        logger.error("boot-sequence - No initial context found for finsemble-service. Stopping boot. Ensure that the finsemble-service entry in systemAppStore contains a valid context entry of type FinsembleServiceContext.");
        return;
    }
    logger.debug("boot-sequence - finsembleServiceContext from finsemble-service.details.context", finsembleServiceContext);
    await glue.contexts.set(FINSEMBLESERVICECONTEXT, finsembleServiceContext);
    try {
        await startServices(glue, finsembleServiceContext, logger, asyncConstructors);
    }
    catch (e) {
        logger.error("boot-sequence - Unhandled exception in service startup", e.toString(), e.stack);
        throw e;
    }
};
if (globalThis.glue42gd) {
    const glueFactory = globalThis.Glue;
    glueFactory?.({ appManager: "full", bus: true, layouts: "full" }).then((glue) => {
        main(glue);
        globalThis.iop = glue;
    });
}
